import { z } from 'zod';
/**
 * Facility RBAC capability taxonomy + role presets (change-list 3.2).
 *
 * This is the SINGLE SOURCE OF TRUTH for facility authorization, shared by the
 * backend (guard + invite validation + OpenAPI) and the frontends (the admin
 * assignment UI + portal hiding). The Prisma `FacilityCapability` enum mirrors
 * the values here; a parity test in the backend pins the two together so they
 * cannot drift.
 *
 * ## The model
 *
 * A facility login holds a SET of capability atoms. A permission check passes
 * when the required atom is in the set — authorization is the UNION of what the
 * login holds, never an intersection. (The multi-role subtlety: someone who is
 * "Coordinator + Payer" sees rates because Payer grants RATE_VIEW, even though a
 * pure Coordinator does not. Compute from the union or the combined-role case
 * silently under-grants.)
 *
 * ## Roles are presets, not a separate concept
 *
 * The four named roles are saved SUBSETS of the atoms, nothing more. v1's admin
 * UI offers these four; the raw per-atom checkbox editor is the v1.x follow-up
 * and needs no schema change — it edits the same array. Keeping roles as data
 * over atoms (not a second enum) is what makes that a pure UI change later.
 *
 * ## Locked taxonomy
 *
 * The atom string values are a contract stored in rows. Adding, removing, or
 * renaming one is a deliberate migration + a preset review, not a code edit —
 * treat this like the audit-event registry.
 *
 * ## Known seam (by design)
 *
 * RATE_VIEW gates SHIFT-level pricing (the billRate on a shift payload).
 * INVOICE_VIEW independently exposes invoice pricing (per-line rates + amounts) —
 * invoices ARE billing documents. So a login with INVOICE_VIEW but NOT RATE_VIEW
 * sees pricing on invoices while its shift billRate is redacted. This is only
 * reachable via an explicit `capabilities` array; the presets always pair them
 * (PAYER holds both), so no named role produces the split. Acceptable: someone
 * you trust with invoices is trusted with the prices on them.
 */
/** The ten capability atoms. SCREAMING_SNAKE to match the Prisma enum exactly
 *  (the parity test asserts value-set equality). */
export const FacilityCapabilitySchema = z.enum([
    'SHIFT_POST',
    'SHIFT_CANCEL',
    'SHIFT_VIEW',
    'SHIFT_SIGN_TIMESHEET',
    'SHIFT_REPORT_NO_SHOW',
    'SHIFT_RATE_CAREGIVER',
    'RATE_VIEW',
    'INVOICE_VIEW',
    'INVOICE_PAY',
    'ACCOUNT_MANAGE',
]);
/** Every atom — the operator-facing full set + the migration backfill target.
 *  Derived from the enum so it can never fall out of sync with it. */
export const ALL_FACILITY_CAPABILITIES = FacilityCapabilitySchema.options;
/** Human-facing labels for the (future v1.x) per-atom checkbox editor and for
 *  operator tooltips. Kept beside the atoms so an added atom without a label
 *  fails the parity test rather than rendering blank. */
export const FACILITY_CAPABILITY_LABELS = {
    SHIFT_POST: 'Post shifts & schedules',
    SHIFT_CANCEL: 'Cancel shifts (may incur a fee)',
    SHIFT_VIEW: 'View shifts & requests',
    SHIFT_SIGN_TIMESHEET: 'Sign timesheets (authorizes the charge)',
    SHIFT_REPORT_NO_SHOW: 'Report a no-show',
    SHIFT_RATE_CAREGIVER: 'Rate care professionals',
    RATE_VIEW: 'View pricing / rates',
    INVOICE_VIEW: 'View invoices',
    // INVOICE_PAY is reserved/forward-wired: no v1 endpoint enforces it
    // (facility-AR payment is operator-managed per Q9). ACCOUNT_MANAGE now gates
    // the facility Admin's self-service user management (`/me/facility/users/*`) —
    // facility Admins manage their own team; the operator only views the roster.
    INVOICE_PAY: 'Pay invoices (operator-managed in v1)',
    ACCOUNT_MANAGE: 'Manage facility users & access',
};
/** The four named role presets. */
export const FacilityRolePresetSchema = z.enum([
    'COORDINATOR',
    'APPROVER',
    'PAYER',
    'ADMIN',
]);
/**
 * Preset → capability set. Derived directly from the 3.2 duties matrix:
 *
 *   · Coordinator fills the roster and never touches money: post/cancel/view
 *     shifts + report no-shows. NO sign-timesheet (that authorizes spend), NO
 *     rate visibility, NO invoices.
 *   · Approver confirms the work and authorizes the charge: view + no-show +
 *     sign-timesheet + rate the caregiver, and sees rates (RATE_VIEW) because
 *     approving a charge blind is not meaningful.
 *   · Payer settles the bill: invoices (view + pay) and rates. Not the roster.
 *   · Admin is everything, including managing other logins.
 *
 * SHIFT_SIGN_TIMESHEET is deliberately in Approver + Admin ONLY (the locked
 * separation-of-duties decision) — scheduling and spend-authorization stay
 * separable. A small facility gives one person both presets; the union then
 * grants signing, without weakening the atom-level rule.
 */
export const FACILITY_ROLE_PRESETS = {
    COORDINATOR: ['SHIFT_POST', 'SHIFT_CANCEL', 'SHIFT_VIEW', 'SHIFT_REPORT_NO_SHOW'],
    APPROVER: [
        'SHIFT_VIEW',
        'SHIFT_REPORT_NO_SHOW',
        'SHIFT_SIGN_TIMESHEET',
        'SHIFT_RATE_CAREGIVER',
        'RATE_VIEW',
    ],
    PAYER: ['INVOICE_VIEW', 'INVOICE_PAY', 'RATE_VIEW'],
    ADMIN: [...ALL_FACILITY_CAPABILITIES],
};
/** Default preset for a newly-invited login when the operator does not pick one
 *  (locked: Coordinator — the least-privilege useful role; the admin ticks up
 *  from there). */
export const DEFAULT_FACILITY_ROLE_PRESET = 'COORDINATOR';
/** Expand a preset to its atom set (a fresh array, safe to mutate). */
export function capabilitiesForPreset(preset) {
    return [...FACILITY_ROLE_PRESETS[preset]];
}
/**
 * The preset a capability set corresponds to, or null if it matches none.
 * Used by the UI to show "Coordinator" instead of four raw checkboxes when the
 * set happens to equal a preset — order-insensitive, exact match.
 */
export function presetForCapabilities(capabilities) {
    const have = new Set(capabilities);
    for (const preset of FacilityRolePresetSchema.options) {
        const want = FACILITY_ROLE_PRESETS[preset];
        if (want.length === have.size && want.every((c) => have.has(c))) {
            return preset;
        }
    }
    return null;
}
//# sourceMappingURL=capability.schema.js.map